import { getIdToken, login } from "../auth/auth.js";

const API = import.meta.env.VITE_API_ENDPOINT;

/** Authenticated fetch: attaches a freshly-refreshed id token, redirects to login
 *  on 401. No silent failures — callers get the parsed body or a thrown error. */
async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await getIdToken();
  if (!token) {
    await login();
    throw new Error("redirecting to login");
  }
  const res = await fetch(`${API}${path}`, {
    ...init,
    headers: {
      ...init.headers,
      authorization: `Bearer ${token}`,
      "content-type": "application/json",
    },
  });
  if (res.status === 401) {
    await login();
    throw new Error("session expired");
  }
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as T;
}

export interface QuizResult {
  email: string;
  quiz_id: string;
  score: number;
  total: number;
  percentage: number;
  submitted_at: string;
}

export const api = {
  submitQuiz: (quiz_id: string, score: number, total: number) =>
    call<{ message: string }>("/quiz", {
      method: "POST",
      body: JSON.stringify({ quiz_id, score, total }),
    }),
  results: (quizId: string) =>
    call<QuizResult[]>(`/quiz-results?quiz_id=${encodeURIComponent(quizId)}`),
  // The listing is the static, CDN-cached files.json (no auth needed); the actual
  // download is gated by a signed URL from /download.
  files: async (): Promise<{ name: string; size: number; last_modified?: string }[]> => {
    const res = await fetch("/files.json", { cache: "no-cache" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  },
  // Locked filenames (also CDN-cached). The download is enforced server-side too;
  // this just drives the UI. Fail open to [] if the lock file is absent.
  fileLocks: async (): Promise<string[]> => {
    try {
      const res = await fetch("/file-locks.json", { cache: "no-cache" });
      if (!res.ok) return [];
      const data: unknown = await res.json();
      const locked = (data as { locked?: unknown }).locked;
      return Array.isArray(locked) ? locked.map(String) : [];
    } catch {
      return [];
    }
  },
  downloadUrl: (file: string) =>
    call<{ url: string }>(`/download?file=${encodeURIComponent(file)}`),
};
